import {
  AppShell,
  Avatar,
  BottomTabs,
  type LaneItem,
  LaneNav,
  type LaneSection,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  ShellMenuButton,
  type ShellPane,
  safeStorage,
  TopBar,
  useIsSinglePane,
} from '@patchbay/ui'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  Archive,
  BadgeCheck,
  Banknote,
  BarChart3,
  BellRing,
  BookOpen,
  Calendar,
  CircleDollarSign,
  ClipboardList,
  Clock,
  FileQuestion,
  FileText,
  FolderCheck,
  FolderKanban,
  FolderOpen,
  Home,
  Hourglass,
  Inbox,
  Lightbulb,
  ListChecks,
  LogOut,
  MessageSquare,
  Play,
  Radio,
  Settings,
  Smartphone,
  Smile,
  Sparkles,
  TrendingUp,
  Truck,
  User,
  Users,
  UsersRound,
} from 'lucide-react'
import * as React from 'react'
import { api as profileApi } from '../features/admin/api'
import { deskTokenLabel, signOut, useMe } from '../features/auth/session'
import { fetchReadyToBill } from '../features/billing/api'
import { BrandSwitcher } from '../features/brands/switcher'
import { useBrandScope } from '../features/brands/use-brand-scope'
import { ChatAvailabilityMenuItem } from '../features/case-view/chat-live'
import { recentDraftCount, remindersQuery } from '../features/closeout/api'
import { HelpMenu } from '../features/help/help-menu'
import { canSeeLicences } from '../features/licences/common'
import { WorkTimer } from '../features/live-work'
import { NewCaseButton } from '../features/new-case/host'
import { NotificationsBell } from '../features/notifications'
import { canSeeProfit } from '../features/profit/api'
import { canSeeProjects, PROJECT_LANES, projectLanesQuery } from '../features/projects/api'
import { QUEUE_LANES } from '../features/queue/lanes'
import { queueLanesQuery } from '../features/queue/queries'
import { WalkthroughBanner } from '../features/riley/walkthrough'
import { describeError, unwrap } from '../lib/api/client'
import type { BrandFeature, LedgerLaneCount } from '../lib/api/generated'
import { ledgerLanesApiV1LedgerLanesGet } from '../lib/api/generated'
import { sessionQuery } from '../lib/api/queries'
import { brandFeatures } from '../lib/brand-features'
import { KEYS } from '../lib/keys'
import { useWorkspace } from '../lib/workspace'
import { ShellBanner, ShellLivenessPill, useShellStatus } from './shell-status'

/** Set by the case view; read by the phone's Case tab. */
export const LAST_CASE_KEY = 'patchbay-last-case'

const LANE_ICON: Record<string, React.ReactNode> = {
  'needs-us-now': <Inbox />,
  'reply-owed': <MessageSquare />,
  'action-owed': <Play />,
  'waiting-customer': <User />,
  'waiting-contractor': <Users />,
  'wrap-up': <ListChecks />,
  'tech-report': <ClipboardList />,
  scheduled: <Calendar />,
  later: <Hourglass />,
  'no-time': <Clock />,
  signals: <Radio />,
  proposals: <FileQuestion />,
  'overdue-by-tech': <UsersRound />,
}

/** `GET /api/v1/ledger/lanes` counts for the nav; its own key so the Ledger screen's snapshot is never overwritten. */
export const navLedgerLanesQuery = () =>
  queryOptions({
    queryKey: ['nav', 'ledger-lanes'],
    queryFn: async ({ signal }): Promise<LedgerLaneCount[]> => {
      const lanes = await unwrap(ledgerLanesApiV1LedgerLanesGet({ signal }))
      if (!Array.isArray(lanes)) throw new Error('ledger lanes answer malformed')
      return lanes
    },
    staleTime: 30_000,
    retry: 1,
  })

/**
 * The left nav, counted from the API: queue lanes from `GET /queue/lanes`, ledger lanes from `GET /ledger/lanes`.
 * A count that failed to load shows "–", never a number; "Overdue by technician" appears only when the API returns
 * it (manager/cover).
 */
export function useSections(): LaneSection[] {
  const me = useMe()
  const brandScope = useBrandScope()
  const enabledFeatures = brandFeatures(me, brandScope.selected)
  const featureOn = (feature: BrandFeature) => enabledFeatures.has(feature)
  const simple = me?.ui_mode === 'simple'
  const queue = useQuery({ ...queueLanesQuery(brandScope.param), enabled: !simple })
  const ledger = useQuery({ ...navLedgerLanesQuery(), enabled: !simple && me?.role !== 'tech' && featureOn('billing') })
  const projectsAllowed = canSeeProjects(me?.role) && !simple
  const projects = useQuery({ ...projectLanesQuery(), enabled: projectsAllowed })
  // Follow-ups (#233): managers and cover only, like `GET /closeout/reminders`; tech and billing never see the entry.
  const dispatcher = me?.role === 'manager' || me?.role === 'cover'
  const adminHref = me?.role === 'cover' ? '/admin/contractors' : me?.role === 'billing' ? '/admin/rates' : '/admin/people'
  const reminders = useQuery({ ...remindersQuery(), enabled: dispatcher && !simple && featureOn('work_orders') })
  const followupsCount: LaneItem['count'] =
    reminders.isError && !reminders.data ? 'unknown' : reminders.data ? recentDraftCount(reminders.data) : undefined
  const queueCount = (api: string): LaneItem['count'] => {
    if (queue.isError && !queue.data) return 'unknown'
    if (!queue.data) return undefined
    const hit = queue.data.lanes.find((l) => l.lane === api)
    return hit ? hit.count : 'unknown'
  }
  const queueUnread = (api: string): number | undefined => queue.data?.lanes.find((l) => l.lane === api)?.unread
  const brandBreakdown = (apis: string[]): string | undefined => {
    if (!brandScope.isAll || !brandScope.showBrands) return undefined
    const totals = new Map<string, number>()
    for (const api of apis) {
      const lane = queue.data?.lanes.find((entry) => entry.lane === api)
      for (const [slug, count] of Object.entries(lane?.by_brand ?? {})) totals.set(slug, (totals.get(slug) ?? 0) + count)
    }
    const labels = new Map(brandScope.brands.map((brand) => [brand.slug, brand.customer_facing_name]))
    return [...totals]
      .filter(([, count]) => count > 0)
      .map(([slug, count]) => `${labels.get(slug) ?? (slug === 'none' ? 'Brand unresolved' : slug)} ${count}`)
      .join(' · ')
  }
  // Nav links carry the brand only when one is chosen in the URL or saved; with nothing selected they are today's hrefs.
  const brandQuery = brandScope.param ? `?brand=${encodeURIComponent(brandScope.param)}` : ''
  const needsUsUnread = (): number | undefined => {
    const replies = queueUnread('reply_owed')
    const actions = queueUnread('action_owed')
    return replies === undefined && actions === undefined ? undefined : (replies ?? 0) + (actions ?? 0)
  }
  const needsUsCount = (): LaneItem['count'] => {
    const replies = queueCount('reply_owed')
    const actions = queueCount('action_owed')
    if (replies === 'unknown' || actions === 'unknown') return 'unknown'
    if (replies === undefined || actions === undefined) return undefined
    return replies + actions
  }
  // #746 1.6: the Ready to bill badge is the /billing list's own total, not the obligations lane (they disagreed).
  const readyToBill = useQuery({
    queryKey: ['billing', 'nav-ready-count'],
    queryFn: async () => {
      const page = await fetchReadyToBill({ view: 'active' })
      return page.total ?? page.items.length
    },
    enabled: !simple && me?.role !== 'tech' && featureOn('billing'),
    staleTime: 30_000,
    retry: 1,
  })
  const readyToBillCount: LaneItem['count'] = readyToBill.isError && readyToBill.data == null ? 'unknown' : readyToBill.data
  const ledgerCount = (lane: LedgerLaneCount['lane']): LaneItem['count'] => {
    if (ledger.isError && !ledger.data) return 'unknown'
    if (!ledger.data) return undefined
    const hit = ledger.data.find((l) => l.lane === lane)
    return hit ? hit.count : 'unknown'
  }
  const projectCount = (lane: (typeof PROJECT_LANES)[number]['lane']): LaneItem['count'] => {
    if (projects.isError && !projects.data) return 'unknown'
    if (!projects.data) return undefined
    const hit = projects.data.lanes.find((entry) => entry.lane === lane)
    return hit ? hit.count : 'unknown'
  }
  const queueItem = (route: string): LaneItem => {
    const def = QUEUE_LANES.find((l) => l.route === route)
    if (!def) throw new Error(`unknown lane ${route}`)
    return {
      id: route,
      label: brandBreakdown([def.api]) ? `${def.label} (${brandBreakdown([def.api])})` : def.label,
      href: `/queue/${route}${brandQuery}`,
      icon: LANE_ICON[route],
      count: queueCount(def.api),
      unread: queueUnread(def.api),
    }
  }
  const overdueByTech = queue.data?.lanes.find((l) => l.lane === 'overdue_by_tech')
  if (simple) {
    return [
      {
        id: 'home',
        items: [
          { id: 'my-day', label: 'My day', href: '/my-day', icon: <Smartphone /> },
          { id: 'notifications', label: 'Notifications', href: '/notifications', icon: <BellRing /> },
          { id: 'knowledge', label: 'Knowledge', href: '/knowledge', icon: <Lightbulb /> },
          { id: 'me', label: 'Me', href: '/me', icon: <User /> },
        ],
      },
    ]
  }
  return [
    { id: 'home', items: [{ id: 'home', label: 'Home', href: '/', icon: <Home />, keys: KEYS.goHome.keys }] },
    {
      id: 'queue',
      label: 'Queue',
      items: [
        {
          id: 'needs-us-now',
          label: brandBreakdown(['reply_owed', 'action_owed'])
            ? `Needs us now (${brandBreakdown(['reply_owed', 'action_owed'])})`
            : 'Needs us now',
          href: `/queue/needs-us-now${brandQuery}`,
          icon: LANE_ICON['needs-us-now'],
          count: needsUsCount(),
          unread: needsUsUnread(),
        },
        ...['waiting-customer', 'waiting-contractor', 'wrap-up', 'tech-report', 'scheduled', 'later', 'no-time', 'signals', 'proposals']
          .filter((route) => {
            const feature = QUEUE_LANES.find((lane) => lane.route === route)?.feature
            return !feature || featureOn(feature)
          })
          .map(queueItem),
        // Every case regardless of lane (#89 item 7): no count, the views carry their own totals.
        { id: 'all-open', label: 'All open', href: `/cases/open${brandQuery}`, icon: <FolderOpen /> },
        { id: 'all-cases', label: 'All cases', href: `/cases/all${brandQuery}`, icon: <Archive /> },
        // #271: recently closed cases with a close-date range; a closed case never sits in a work lane
        { id: 'closed', label: 'Closed', href: `/cases/closed${brandQuery}`, icon: <FolderCheck /> },
      ],
    },
    ...(projectsAllowed && projects.data
      ? [
          {
            id: 'projects',
            label: 'Projects',
            items: PROJECT_LANES.map((lane, index) => ({
              id: `projects-${lane.lane}`,
              label: projects.data?.lanes.find((entry) => entry.lane === lane.lane)?.label ?? lane.label,
              href: index === 0 ? '/projects' : `/projects/${lane.slug}`,
              icon: <FolderKanban />,
              count: projectCount(lane.lane),
              keys: index === 0 ? KEYS.goProjects.keys : undefined,
            })),
          },
        ]
      : projectsAllowed && projects.isError
        ? [
            {
              id: 'projects',
              label: 'Projects',
              items: [
                {
                  id: 'projects-unreachable',
                  label: 'Projects unreachable',
                  href: '/projects',
                  icon: <FolderKanban />,
                  count: 'unknown' as const,
                },
              ],
            },
          ]
        : []),
    ...(me?.role === 'tech' || (!featureOn('billing') && !featureOn('licences'))
      ? []
      : [
          {
            id: 'ledger',
            label: 'Ledger',
            items: [
              ...(featureOn('billing')
                ? [
                    {
                      id: 'awaiting-invoice',
                      label: 'Awaiting invoice',
                      href: '/ledger',
                      icon: <FileText />,
                      count: ledgerCount('awaiting_invoice'),
                    },
                    {
                      id: 'unmatched-invoices',
                      label: 'Unmatched invoices',
                      href: '/ledger',
                      icon: <CircleDollarSign />,
                      count: ledgerCount('unmatched_invoices'),
                    },
                    {
                      id: 'billing',
                      label: 'Ready to bill',
                      href: '/billing',
                      icon: <ListChecks />,
                      count: readyToBillCount,
                      keys: KEYS.goBilling.keys,
                    },
                    {
                      id: 'payment-owed',
                      label: 'Payment owed',
                      href: '/ledger/payment-owed',
                      icon: <Banknote />,
                      count: ledgerCount('payment_owed'),
                    },
                    { id: 'ledger', label: 'Everything owed', href: '/ledger', icon: <BookOpen />, keys: KEYS.goLedger.keys },
                  ]
                : []),
              // Manager and billing only (COST_READERS): absent, not disabled, for cover and tech.
              ...(featureOn('billing') && canSeeProfit(me?.role)
                ? [{ id: 'profit', label: 'Profitability', href: '/profit', icon: <TrendingUp />, keys: KEYS.goProfit.keys }]
                : []),
              // Pax8 licences and reconciliation (#1111): manager and billing only, like the route's beforeLoad.
              ...(featureOn('licences') && canSeeLicences(me?.role)
                ? [{ id: 'licences', label: 'Licences', href: '/licences', icon: <BadgeCheck /> }]
                : []),
            ],
          },
        ]),
    {
      id: 'team',
      label: 'Team',
      items: [
        ...(overdueByTech && featureOn('contractors')
          ? [{ ...queueItem('overdue-by-tech'), count: overdueByTech.count, alert: true }]
          : []),
        // The count is shadow drafts from the last week: what is waiting for review before a sender goes to auto.
        ...(dispatcher && featureOn('work_orders')
          ? [{ id: 'followups', label: 'Follow-ups', href: '/followups', icon: <BellRing />, count: followupsCount }]
          : []),
        ...(dispatcher && featureOn('schedule')
          ? [{ id: 'schedule', label: 'Schedule', href: '/schedule', icon: <Truck />, keys: KEYS.goDispatch.keys }]
          : []),
        ...(dispatcher && featureOn('satisfaction')
          ? [{ id: 'satisfaction', label: 'Satisfaction', href: '/satisfaction', icon: <Smile /> }]
          : []),
        { id: 'knowledge', label: 'Knowledge', href: '/knowledge', icon: <Lightbulb />, keys: KEYS.goKnowledge.keys },
        // #1115: every staff role; the catalogue lists only the reports a role may open.
        { id: 'reports', label: 'Reports', href: '/reports', icon: <BarChart3 />, keys: KEYS.goReports.keys },
        ...(me?.role === 'manager' || (me?.role === 'cover' && featureOn('contractors')) || (me?.role === 'billing' && featureOn('billing'))
          ? [{ id: 'admin', label: me.role === 'cover' ? 'Contractors' : 'Admin', href: adminHref, icon: <Settings /> }]
          : []),
        { id: 'my-day', label: 'My day (phone)', href: '/my-day', icon: <Smartphone /> },
      ],
    },
  ]
}

export function UserMenu() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()
  const [failure, setFailure] = React.useState<string | null>(null)
  const setMode = async () => {
    if (!me) return
    setFailure(null)
    const next = me.ui_mode === 'simple' ? 'full' : 'simple'
    try {
      const saved = await profileApi.putUiMode(next, me.csrf_token)
      queryClient.setQueryData(['me'], saved)
      queryClient.setQueryData(sessionQuery().queryKey, (old) => {
        if (!old?.signed_in || !old.me) return old
        const current = old.me
        return { ...old, me: { ...current, ...saved, calendar_token: saved.calendar_token ?? current.calendar_token } }
      })
      navigate({ to: next === 'simple' ? '/my-day' : '/' })
    } catch (err) {
      setFailure(`Mode unchanged: ${describeError(err)}`)
    }
  }
  return (
    <Menu>
      <MenuTrigger
        aria-label="Account"
        data-slot="shell-icon"
        className="inline-flex cursor-pointer items-center gap-2 rounded-full p-0.5 hover:bg-muted max-pane:size-11 max-pane:justify-center max-pane:p-0"
      >
        <Avatar name={me?.name || me?.email || '?'} size="sm" />
      </MenuTrigger>
      <MenuContent>
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          {me ? `${me.name || me.email} · ${me.role}` : 'Not confirmed (API unreachable)'}
        </div>
        {failure ? <div className="px-2 pb-1.5 text-xs text-destructive">{failure}</div> : null}
        <ChatAvailabilityMenuItem />
        <MenuSeparator />
        <MenuItem onClick={() => navigate({ to: '/me' })}>
          <User /> Me, signature, theme
        </MenuItem>
        {me ? (
          <MenuItem onClick={() => void setMode()}>
            <Smartphone />
            {me.ui_mode === 'simple' ? 'View full workspace' : me.role === 'manager' ? 'View as tech' : 'Use simple mode'}
          </MenuItem>
        ) : null}
        {me?.role === 'manager' || me?.role === 'cover' ? (
          <MenuItem onClick={() => navigate({ to: me.role === 'cover' ? '/admin/contractors' : '/admin/people' })}>
            <Settings /> {me.role === 'cover' ? 'Contractors' : 'Admin'}
          </MenuItem>
        ) : null}
        <MenuSeparator />
        <MenuItem
          onClick={async () => {
            setFailure(null)
            try {
              await signOut(queryClient)
              navigate({ to: '/signin' })
            } catch (err) {
              setFailure(`Not signed out: ${describeError(err)}`)
            }
          }}
        >
          <LogOut /> Sign out
        </MenuItem>
      </MenuContent>
    </Menu>
  )
}

export function Frame({
  active,
  main,
  rail,
  banner,
  railless = false,
  allowFull = true,
}: {
  active: string
  main: React.ReactNode
  rail?: React.ReactNode
  banner?: React.ReactNode
  /** Full-width screens (board, billing grid) still allow the rail to be opened with `r`. */
  railless?: boolean
  /** Home's right pane is the Today column, not Riley, so it never goes full-size. */
  allowFull?: boolean
}) {
  const ws = useWorkspace()
  const navigate = useNavigate()
  const sections = useSections()
  const status = useShellStatus()
  const me = useMe()
  const [mobilePane, setMobilePane] = React.useState<ShellPane>('main')
  const [navOpen, setNavOpen] = React.useState(false)
  const navTouchStart = React.useRef<{ x: number; y: number } | null>(null)
  const single = useIsSinglePane()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const simple = me?.ui_mode === 'simple'
  const home = active === 'home'
  const caseOpen = pathname.startsWith('/case/')
  const queueOpen = pathname.startsWith('/queue/') || pathname.startsWith('/cases/')
  // The path is the trigger: a lane link should leave the drawer closed on the next screen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger, not a value the effect reads
  React.useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  return (
    <AppShell
      layoutId={`patchbay:${railless ? 'wide' : 'std'}`}
      navOpen={single && navOpen}
      onNavOpenChange={setNavOpen}
      topBar={
        <TopBar
          brand={<BrandSwitcher />}
          leading={single ? <ShellMenuButton open={navOpen} onOpen={() => setNavOpen(true)} /> : null}
          onOpenPalette={() => ws.setPaletteOpen(true)}
          liveness={<ShellLivenessPill status={status} />}
          right={
            <>
              <NewCaseButton />
              {me ? <NotificationsBell /> : null}
              <button
                type="button"
                onClick={() => ws.setRileyMode(ws.rileyMode === 'full' ? 'rail' : 'full')}
                title="Riley full-size (Shift+R)"
                aria-label="Toggle Riley full-size"
                className="hidden size-8 cursor-pointer items-center justify-center rounded-md text-riley hover:bg-riley-soft md:inline-flex"
              >
                <Sparkles className="size-4" />
              </button>
              <HelpMenu onKeySheet={() => ws.setKeySheetOpen(true)} />
              <UserMenu />
            </>
          }
        />
      }
      banner={
        <>
          {banner ?? <ShellBanner status={status} />}
          {/* #1014: my work timer, on every screen; the server owns it, so refresh and navigation keep it. */}
          {me ? <WorkTimer /> : null}
          <WalkthroughBanner />
        </>
      }
      nav={
        <LaneNav
          sections={sections}
          activeId={active}
          renderLink={({ href, children, ...rest }) => (
            <Link
              to={href as '/'}
              {...rest}
              onTouchStart={(event) => {
                const touch = event.touches.item(0)
                navTouchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null
              }}
              onTouchEnd={(event) => {
                const start = navTouchStart.current
                const touch = event.changedTouches.item(0)
                navTouchStart.current = null
                if (!single || !start || !touch || Math.hypot(touch.clientX - start.x, touch.clientY - start.y) > 8) return
                // Base UI's modal layer can consume the synthetic click that follows a tap. Commit a stationary
                // touch directly so every drawer destination, including Home, works on a phone.
                event.preventDefault()
                setNavOpen(false)
                navigate({ to: href as '/' })
              }}
            >
              {children}
            </Link>
          )}
          footer={
            <div className="flex items-center gap-2 px-1.5 py-1">
              <Avatar name={me?.name || me?.email || '?'} size="sm" />
              <div className="min-w-0 flex-1 text-xs">
                <p className="truncate font-medium">{me ? me.name || me.email : 'Signed-in user unknown'}</p>
                {me ? (
                  <p className={me.desk_token === 'connected' || !me.can_write_desk ? 'text-success' : 'text-warning'}>
                    {deskTokenLabel(me)}
                  </p>
                ) : (
                  <p className="text-subtle">API unreachable</p>
                )}
              </div>
            </div>
          }
        />
      }
      main={main}
      rail={rail}
      railOpen={ws.railOpen && Boolean(rail)}
      railFull={allowFull && ws.rileyMode === 'full'}
      mobilePane={mobilePane}
      bottomTabs={
        <BottomTabs
          items={[
            {
              id: 'queue',
              label: simple ? 'My day' : 'Queue',
              icon: <Inbox />,
              active: mobilePane === 'main' && (simple ? pathname === '/my-day' : queueOpen),
              onSelect: () => {
                setMobilePane('main')
                if (simple) navigate({ to: '/my-day' })
                else navigate({ to: '/queue/$lane', params: { lane: 'needs-us-now' } })
              },
            },
            {
              id: 'case',
              label: 'Case',
              icon: <FileText />,
              active: mobilePane === 'main' && caseOpen,
              onSelect: () => {
                setMobilePane('main')
                // The case this browser opened last; without one, All open is the explicit case picker.
                const last = safeStorage.getItem(LAST_CASE_KEY)
                if (last) navigate({ to: '/case/$id', params: { id: last } })
                else if (simple) navigate({ to: '/my-day' })
                else navigate({ to: '/cases/open' })
              },
            },
            {
              id: 'riley',
              label: home ? 'Today' : 'Riley',
              icon: home ? <Home /> : <Sparkles />,
              active: mobilePane === 'rail',
              onSelect: () => setMobilePane('rail'),
            },
            {
              id: 'me',
              label: 'Me',
              icon: <User />,
              active: active === 'me',
              onSelect: () => {
                setMobilePane('main')
                navigate({ to: '/me' })
              },
            },
          ]}
        />
      }
    />
  )
}

/** Page header inside the centre pane. */
export function PaneHeader({
  title,
  subtitle,
  actions,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
}) {
  // The title block never shrinks below its text (no "Repl…" at 1440 px with the rail open); the actions wrap
  // onto their own line instead.
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3.5 sm:px-5">
      <div className="min-w-0 max-w-full flex-[1_0_auto] max-pane:flex-[1_1_auto]">
        <h1 className="truncate whitespace-nowrap text-2xl font-bold tracking-tight">{title}</h1>
        {subtitle ? <p className="truncate whitespace-nowrap text-sm text-muted-foreground">{subtitle}</p> : null}
      </div>
      {actions ? <div className="ml-auto flex min-w-0 max-w-full flex-wrap justify-end">{actions}</div> : null}
    </div>
  )
}
